<template>
    <div class="op-th" :class="thClass">

        <!-- Label + icona sort -->
        <div class="op-th-label" v-bind="$attrs" @click="onLabelClick">
            <slot />
            <span class="op-sort-icon" v-if="sort">
                <span v-if="isSortedAsc">↑</span>
                <span v-else-if="isSortedDesc">↓</span>
                <span v-else class="op-sort-neutral">↕</span>
            </span>
        </div>

        <!-- Handle resize (disabilitato su dispositivi touch) -->
        <div
            v-if="resizable !== false && !isLastColumn && !isTouchDevice"
            class="op-resize-handle"
            @mousedown.prevent.stop="startResize"
            @dblclick.prevent.stop="resetWidth"
        />

    </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { OP_TABLE_CTX, type OpTableContext, type SortDir } from './opTable.context';
import { isTouchDevice } from '@/utils/device';

@Options({
    inject: {
        opTableCtx: { from: OP_TABLE_CTX as any, default: null },
    },
})
export default class OpTh extends Vue {
    @Prop() width?: number | string;
    @Prop() minWidth?: number | string;
    @Prop() maxWidth?: number | string;
    @Prop({ default: true }) resizable!: boolean;
    @Prop() sort?: string;

    opTableCtx!: OpTableContext | null;

    colIndex = -1;
    isResizing = false;
    isTouchDevice: boolean = isTouchDevice();

    // ─── Lifecycle ───────────────────────────────────────────────────

    created() {
        if (this.opTableCtx) {
            this.colIndex = this.opTableCtx.registerColumn({
                width: this.width,
                minWidth: this.minWidth,
                maxWidth: this.maxWidth,
                resizable: this.resizable,
            });
        }
    }

    // ─── Computed ────────────────────────────────────────────────────

    get sortState(): { key?: string; dir: SortDir } {
        return this.opTableCtx?.getSort() ?? { key: undefined, dir: '' };
    }

    get isSortedAsc(): boolean {
        return !!this.sort && this.sortState.key === this.sort && this.sortState.dir === 'asc';
    }

    get isSortedDesc(): boolean {
        return !!this.sort && this.sortState.key === this.sort && this.sortState.dir === 'desc';
    }

    get isLastColumn(): boolean {
        return this.colIndex >= 0 && this.colIndex === (this.opTableCtx?.getColumnCount() ?? 0) - 1;
    }

    get thClass(): Record<string, boolean> {
        return {
            'op-th--sortable': !!this.sort,
            'op-th--sorted': this.isSortedAsc || this.isSortedDesc,
            'op-th--resizing': this.isResizing,
        };
    }

    // ─── Sort ────────────────────────────────────────────────────────

    onLabelClick() {
        if (!this.sort || !this.opTableCtx) return;

        const { key, dir } = this.sortState;
        let next: SortDir = 'asc';

        if (key === this.sort) {
            // Ciclo: nessuno → asc → desc → nessuno
            next = dir === 'asc' ? 'desc' : dir === 'desc' ? '' : 'asc';
        }

        this.opTableCtx.setSort(this.sort, next);
    }

    // ─── Resize ──────────────────────────────────────────────────────

    resetWidth() {
        if (!this.opTableCtx || this.resizable === false || this.isTouchDevice) return;
        this.opTableCtx.resetColumnWidth(this.colIndex);
    }

    startResize(e: MouseEvent) {
        if (!this.opTableCtx || this.resizable === false || this.isTouchDevice) return;

        this.isResizing = true;

        const startX = e.clientX;
        // Leggo la larghezza attuale dalla DOM (= valore reale renderizzato dalla CSS var)
        const startWidth = (this.$el as HTMLElement).offsetWidth;

        const onMove = (ev: MouseEvent) => {
            const newWidth = startWidth + (ev.clientX - startX);
            this.opTableCtx!.updateColumnWidth(this.colIndex, newWidth);
        };

        const onUp = () => {
            this.isResizing = false;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }
}
</script>
